import { DIGITAL_LOAN_STEPS } from 'constants/digitalLoan';
import {
  MANUAL_CATALOG_LEVELS,
  MANUAL_MODEL_CODE,
  MANUAL_NAME_MAX_LENGTH,
  MANUAL_OTHER_NAME,
  OTHER_OPTION_ID,
} from 'constants/digitalLoanPhase2';

const OTHER_LABEL_PATTERN = /^(otro|otros|otra|otras)$/;

/**
 * Normaliza un nombre de catálogo para comparar OTRO/OTROS/OTRA.
 */
export function normalizeCatalogLabel(name = '') {
  return String(name)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

/**
 * True si el label es OTRO/OTROS/OTRA (con o sin acento).
 */
export function isOtherCatalogLabel(name = '') {
  return OTHER_LABEL_PATTERN.test(normalizeCatalogLabel(name));
}

/**
 * True si el item es el sentinel UI de OTRO (nunca un código SAP).
 */
export function isUiOtherOption(item) {
  return item?.isManual === true || item?.id === OTHER_OPTION_ID;
}

/**
 * Solo el sentinel UI dispara captura manual.
 * Un renglón SAP/catálogo llamado OTRO/OTROS/OTRA sigue catálogo.
 */
export function isCatalogOtherSelection(item) {
  return isUiOtherOption(item);
}

/**
 * Mayúsculas sin acentos. No recorta espacios: el usuario sigue
 * pudiendo escribir varias palabras. Ñ se conserva. Ü pasa a U.
 */
export function normalizeCollateralText(value = '') {
  return String(value || '')
    .toLocaleUpperCase('es-MX')
    .replace(/[ÁÀÂÄ]/g, 'A')
    .replace(/[ÉÈÊË]/g, 'E')
    .replace(/[ÍÌÎÏ]/g, 'I')
    .replace(/[ÓÒÔÖ]/g, 'O')
    .replace(/[ÚÙÛÜ]/g, 'U')
    .slice(0, MANUAL_NAME_MAX_LENGTH);
}

/**
 * Limita nombres manuales sin quitar espacios internos.
 * El trim de extremos ocurre al validar o al armar /guarantee.
 */
export function sanitizeManualCatalogName(value = '') {
  return normalizeCollateralText(value);
}

export function emptyManualCatalogValues() {
  return {
    familyName: '',
    subfamilyName: '',
    articleName: '',
    brandName: '',
    modelName: '',
  };
}

export function sanitizeManualCatalogValues(values = {}) {
  const empty = emptyManualCatalogValues();
  return Object.keys(empty).reduce((acc, key) => {
    acc[key] = sanitizeManualCatalogName(values[key]);
    return acc;
  }, {});
}

export function createManualOtherItem(overrides = {}) {
  return {
    id: OTHER_OPTION_ID,
    name: MANUAL_OTHER_NAME,
    isManual: true,
    ...overrides,
  };
}

/**
 * Agrega OTRO al final si el API no trajo OTRO/OTROS/OTRA.
 * El id "other" es solo UI; no debe ir al payload de guarantee.
 */
export function withManualOtherOption(items = [], { name = MANUAL_OTHER_NAME } = {}) {
  const list = Array.isArray(items) ? items : [];
  const hasOther = list.some(
    (item) => item?.id === OTHER_OPTION_ID || isOtherCatalogLabel(item?.name),
  );
  if (hasOther) return list;
  return [...list, createManualOtherItem({ name })];
}

export function isManualCatalogDetailLevel(level) {
  return (
    level === MANUAL_CATALOG_LEVELS.FAMILY
    || level === MANUAL_CATALOG_LEVELS.SUBFAMILY
    || level === MANUAL_CATALOG_LEVELS.ARTICLE
    || level === MANUAL_CATALOG_LEVELS.BRAND
  );
}

/**
 * Campos required del formulario manual.
 * Backend: familia/artículo/marca required; subfamilia y modelo
 * opcionales. El modelo se mantiene required en UI (tickets 2+10)
 * para no perder datos que la IA usa al valuar.
 */
export function getRequiredManualCatalogFields(level) {
  if (level === MANUAL_CATALOG_LEVELS.FAMILY) {
    return ['familyName', 'articleName', 'brandName', 'modelName'];
  }
  if (
    level === MANUAL_CATALOG_LEVELS.SUBFAMILY
    || level === MANUAL_CATALOG_LEVELS.ARTICLE
  ) {
    return ['articleName', 'brandName', 'modelName'];
  }
  if (level === MANUAL_CATALOG_LEVELS.BRAND) {
    return ['brandName', 'modelName'];
  }
  return ['modelName'];
}

export function areManualCatalogValuesComplete(level, values = {}) {
  return getRequiredManualCatalogFields(level).every(
    (key) => String(values[key] || '').trim(),
  );
}

export function getManualCatalogBackStep(level) {
  switch (level) {
    case MANUAL_CATALOG_LEVELS.FAMILY:
      return DIGITAL_LOAN_STEPS.GUARANTEE_FAMILY;
    case MANUAL_CATALOG_LEVELS.SUBFAMILY:
      return DIGITAL_LOAN_STEPS.GUARANTEE_SUBFAMILY;
    case MANUAL_CATALOG_LEVELS.ARTICLE:
      return DIGITAL_LOAN_STEPS.GUARANTEE_ARTICLE;
    case MANUAL_CATALOG_LEVELS.BRAND:
    case MANUAL_CATALOG_LEVELS.MODEL:
      return DIGITAL_LOAN_STEPS.GUARANTEE_BRAND;
    default:
      return DIGITAL_LOAN_STEPS.GUARANTEE_MODEL;
  }
}

/**
 * Evita consumir catálogos posteriores cuando el usuario eligió OTRO.
 */
export function shouldSkipCatalogEndpoint(phase2, endpoint) {
  const state = phase2 || {};
  const level = state.manualCatalogStartLevel;
  const familyOther = isUiOtherOption(state.selectedFamily);
  const subOther = isUiOtherOption(state.selectedSubFamily);
  const articleOther = isUiOtherOption(state.selectedArticle);
  const brandOther = isUiOtherOption(state.selectedBrand);
  const skippedByLevel = isManualCatalogDetailLevel(level);

  if (endpoint === 'subFamilies') {
    return familyOther || level === MANUAL_CATALOG_LEVELS.FAMILY;
  }
  if (endpoint === 'articles') {
    return familyOther || subOther || skippedByLevel;
  }
  if (endpoint === 'brands') {
    return familyOther || subOther || articleOther || skippedByLevel;
  }
  if (endpoint === 'models') {
    return familyOther || subOther || articleOther || brandOther || skippedByLevel;
  }
  return false;
}

export function patchManualCatalogValues(phase2, patch) {
  const values = sanitizeManualCatalogValues({
    ...emptyManualCatalogValues(),
    ...phase2.manualCatalogValues,
    ...patch,
  });
  const level = phase2.manualCatalogStartLevel;
  const next = { ...phase2, manualCatalogValues: values };

  if (
    level === MANUAL_CATALOG_LEVELS.FAMILY
    || level === MANUAL_CATALOG_LEVELS.SUBFAMILY
  ) {
    values.subfamilyName = MANUAL_OTHER_NAME;
    next.manualCatalogValues = values;
  }

  if (level === MANUAL_CATALOG_LEVELS.FAMILY) {
    next.selectedFamily = createManualOtherItem({
      name: values.familyName,
    });
    next.selectedSubFamily = createManualOtherItem({
      name: MANUAL_OTHER_NAME,
    });
  } else if (level === MANUAL_CATALOG_LEVELS.SUBFAMILY) {
    next.selectedSubFamily = createManualOtherItem({
      name: MANUAL_OTHER_NAME,
    });
  }

  if (
    level === MANUAL_CATALOG_LEVELS.FAMILY
    || level === MANUAL_CATALOG_LEVELS.SUBFAMILY
    || level === MANUAL_CATALOG_LEVELS.ARTICLE
  ) {
    next.selectedArticle = createManualOtherItem({
      name: values.articleName,
    });
    next.selectedBrand = createManualOtherItem({
      name: values.brandName,
    });
  } else if (level === MANUAL_CATALOG_LEVELS.BRAND) {
    next.selectedBrand = createManualOtherItem({
      name: values.brandName,
    });
  }

  next.selectedModel = {
    id: MANUAL_MODEL_CODE,
    name: values.modelName,
    isManual: true,
  };
  return next;
}

/**
 * Limpia OTRO y lo posterior al volver al nivel donde se eligió.
 */
export function clearManualCatalogFromLevel(phase2) {
  const level = phase2.manualCatalogStartLevel;
  const reset = {
    selectedModel: null,
    manualCatalogStartLevel: null,
    manualCatalogValues: emptyManualCatalogValues(),
    productCondition: '',
    estimatedValue: '',
    estimatedValueLocked: false,
  };

  if (level === MANUAL_CATALOG_LEVELS.FAMILY) {
    return {
      ...phase2,
      ...reset,
      selectedFamily: null,
      selectedSubFamily: null,
      selectedArticle: null,
      selectedBrand: null,
    };
  }
  if (level === MANUAL_CATALOG_LEVELS.SUBFAMILY) {
    return {
      ...phase2,
      ...reset,
      selectedSubFamily: null,
      selectedArticle: null,
      selectedBrand: null,
    };
  }
  if (level === MANUAL_CATALOG_LEVELS.ARTICLE) {
    return {
      ...phase2,
      ...reset,
      selectedArticle: null,
      selectedBrand: null,
    };
  }
  if (level === MANUAL_CATALOG_LEVELS.BRAND) {
    return {
      ...phase2,
      ...reset,
      selectedBrand: null,
    };
  }
  return { ...phase2, ...reset };
}
